import React, { Component, createRef } from 'react';
import {
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import config from '../../../config';

class DriverPayTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      records: [],
      grandTotal: 0,
      date: '',
      lastUpdated: null,
      isLoading: false,
      isRefreshing: false,
      isGeneratingExcel: false,
      isGeneratingDriverCsv: false,
      isGeneratingJoiningBonusCsv: false,
      excelGenerated: false,
      excelGeneratedAt: null,
      error: null,
      refreshResult: null,
      expandedRows: new Set(),
      // Payout upload
      payoutFile: null,
      isUploading: false,
      payoutResult: null,
      payoutError: null,
      // Cash payments
      cashModalOpen: false,
      cashMode: 'all_today',
      cashOperationDate: new Date().toISOString().slice(0, 10),
      vehicleIdInput: '',
      vehicleCashMode: 'all_trips',
      vehicleTrips: [],
      selectedTripId: '',
      isLoadingVehicleTrips: false,
      isSavingCashPayment: false,
      cashActionMessage: null,
      cashActionError: null,
    };
    this.fileInputRef = createRef();
  }

  componentDidMount() {
    this.fetchTodayData();
  }

  fetchTodayData = async () => {
    this.setState({ isLoading: true, error: null });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.opsApiBase}/payments/eod-driver-pay/today`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      this.setState({
        records: data.records || [],
        grandTotal: data.grand_total || 0,
        date: data.date || '',
        lastUpdated: new Date(),
        excelGenerated: data.excel_generated || false,
        excelGeneratedAt: data.excel_generated_at || null,
        isLoading: false,
      });
    } catch (err) {
      this.setState({ error: err.message, isLoading: false });
    }
  };

  handleRefresh = async () => {
    this.setState({ isRefreshing: true, error: null, refreshResult: null });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.opsApiBase}/payments/eod-driver-pay/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || `Server error: ${res.status}`);
      }
      const data = await res.json();
      this.setState({
        records: data.records || [],
        grandTotal: data.grand_total_pay || 0,
        date: data.date || '',
        lastUpdated: new Date(),
        isRefreshing: false,
        refreshResult: {
          driversProcessed: data.drivers_processed,
          deletedRows: data.deleted_rows,
          resetBonusRows: data.reset_bonus_rows,
          grandTotal: data.grand_total_pay,
        },
      });
    } catch (err) {
      this.setState({ error: err.message, isRefreshing: false });
    }
  };

  handleGenerateExcel = async () => {
    this.setState({ isGeneratingExcel: true, error: null });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.opsApiBase}/payments/eod-driver-pay/generate-excel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const err = await res.json();
          throw new Error(err.detail || `Server error: ${res.status}`);
        }
        const errText = await res.text();
        throw new Error(errText || `Server error: ${res.status}`);
      }

      const contentType = (res.headers.get('content-type') || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const data = await res.json();
        const files = data.files || [];

        files.forEach((file) => {
          const blob = new Blob([file.content || ''], { type: 'text/csv;charset=utf-8;' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.filename || `driver_pay_${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        });
      } else {
        // backward fallback (single file response)
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `driver_pay_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }

      // Lock the UI
      this.setState({
        isGeneratingExcel: false,
        excelGenerated: true,
        excelGeneratedAt: new Date().toISOString(),
      });
    } catch (err) {
      this.setState({ error: err.message, isGeneratingExcel: false });
    }
  };

  handleGenerateJoiningBonusCsv = async () => {
    this.setState({ isGeneratingJoiningBonusCsv: true, error: null });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.opsApiBase}/payments/joining-bonus/generate-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const err = await res.json();
          throw new Error(err.detail || `Server error: ${res.status}`);
        }
        const errText = await res.text();
        throw new Error(errText || `Server error: ${res.status}`);
      }

      const data = await res.json();
      const files = data.files || [];

      files.forEach((file) => {
        const blob = new Blob([file.content || ''], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.filename || `joining_bonus_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      });

      this.setState({ isGeneratingJoiningBonusCsv: false });
    } catch (err) {
      this.setState({ error: err.message, isGeneratingJoiningBonusCsv: false });
    }
  };

  handleGenerateDriverCsv = async () => {
    const input = window.prompt('Enter vehicle ID');
    if (input === null) return;

    const vehicleId = parseInt(String(input).trim(), 10);
    if (!Number.isInteger(vehicleId) || vehicleId <= 0) {
      this.setState({ error: 'Please enter a valid vehicle ID' });
      return;
    }

    this.setState({ isGeneratingDriverCsv: true, error: null });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${config.opsApiBase}/payments/eod-driver-pay/generate-driver-excel?vehicle_id=${vehicleId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const err = await res.json();
          throw new Error(err.detail || `Server error: ${res.status}`);
        }
        const errText = await res.text();
        throw new Error(errText || `Server error: ${res.status}`);
      }

      const data = await res.json();
      const files = data.files || [];

      files.forEach((file) => {
        const blob = new Blob([file.content || ''], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.filename || `driver_${vehicleId}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      });

      this.setState({ isGeneratingDriverCsv: false });
    } catch (err) {
      this.setState({ error: err.message, isGeneratingDriverCsv: false });
    }
  };

  handlePayoutFileChange = (e) => {
    const file = e.target.files[0] || null;
    this.setState({ payoutFile: file, payoutResult: null, payoutError: null });
  };

  handlePayoutUpload = async () => {
    const { payoutFile } = this.state;
    if (!payoutFile) return;

    this.setState({ isUploading: true, payoutResult: null, payoutError: null });
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('csv_file', payoutFile);

      const res = await fetch(`${config.opsApiBase}/driver-payout/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `Server error: ${res.status}`);
      }
      this.setState({ payoutResult: data, isUploading: false });
      // Reset file input
      if (this.fileInputRef.current) this.fileInputRef.current.value = '';
      this.setState({ payoutFile: null });
    } catch (err) {
      this.setState({ payoutError: err.message, isUploading: false });
    }
  };

  toggleCashModal = () => {
    this.setState((prev) => ({
      cashModalOpen: !prev.cashModalOpen,
      cashActionMessage: prev.cashModalOpen ? null : prev.cashActionMessage,
      cashActionError: prev.cashModalOpen ? null : prev.cashActionError,
    }));
  };

  loadVehicleTrips = async () => {
    const vehicleId = parseInt(String(this.state.vehicleIdInput || '').trim(), 10);
    const selectedDate = this.state.cashOperationDate;
    if (!Number.isInteger(vehicleId) || vehicleId <= 0) {
      this.setState({ cashActionError: 'Please enter a valid vehicle ID' });
      return;
    }
    if (!selectedDate) {
      this.setState({ cashActionError: 'Please select a date' });
      return;
    }

    this.setState({ isLoadingVehicleTrips: true, cashActionError: null, cashActionMessage: null });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${config.opsApiBase}/payments/cash-payments/vehicle-trips?vehicle_id=${vehicleId}&operation_date=${encodeURIComponent(selectedDate)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `Server error: ${res.status}`);
      }

      this.setState({
        vehicleTrips: data.trips || [],
        selectedTripId: '',
        isLoadingVehicleTrips: false,
        cashActionMessage: `Loaded ${data.trips?.length || 0} trip(s) for vehicle ${vehicleId}`,
      });
    } catch (err) {
      this.setState({ isLoadingVehicleTrips: false, cashActionError: err.message });
    }
  };

  handleCashPaymentSubmit = async () => {
    const { cashMode, vehicleCashMode, vehicleIdInput, selectedTripId, cashOperationDate } = this.state;

    if (!cashOperationDate) {
      this.setState({ cashActionError: 'Please select a date' });
      return;
    }

    this.setState({ isSavingCashPayment: true, cashActionError: null, cashActionMessage: null });
    try {
      const token = localStorage.getItem('token');
      let url = '';
      let method = 'POST';
      let payload = null;

      if (cashMode === 'all_today') {
        url = `${config.opsApiBase}/payments/cash-payments/mark-all-today?operation_date=${encodeURIComponent(cashOperationDate)}`;
      } else {
        const vehicleId = parseInt(String(vehicleIdInput || '').trim(), 10);
        if (!Number.isInteger(vehicleId) || vehicleId <= 0) {
          throw new Error('Please enter a valid vehicle ID');
        }

        if (vehicleCashMode === 'all_trips') {
          url = `${config.opsApiBase}/payments/cash-payments/mark-vehicle-all?operation_date=${encodeURIComponent(cashOperationDate)}`;
          payload = { vehicle_id: vehicleId };
        } else {
          const tripId = parseInt(String(selectedTripId || '').trim(), 10);
          if (!Number.isInteger(tripId) || tripId <= 0) {
            throw new Error('Please select a trip');
          }
          url = `${config.opsApiBase}/payments/cash-payments/mark-trip?operation_date=${encodeURIComponent(cashOperationDate)}`;
          payload = { vehicle_id: vehicleId, trip_id: tripId };
        }
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: payload ? JSON.stringify(payload) : undefined,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `Server error: ${res.status}`);
      }

      this.setState({
        isSavingCashPayment: false,
        cashActionMessage: data.message || 'Cash payment update completed',
      });

      await this.fetchTodayData();

      if (cashMode === 'specific_driver' && vehicleCashMode === 'particular_trip') {
        await this.loadVehicleTrips();
      }
    } catch (err) {
      this.setState({ isSavingCashPayment: false, cashActionError: err.message });
    }
  };

  formatCurrency = (val) => `₹${parseFloat(val || 0).toFixed(2)}`;
  formatKm = (val) => `${parseFloat(val || 0).toFixed(1)} km`;

  toggleRow = (driverId) => {
    this.setState(prev => {
      const next = new Set(prev.expandedRows);
      next.has(driverId) ? next.delete(driverId) : next.add(driverId);
      return { expandedRows: next };
    });
  };

  render() {
    const {
      records, grandTotal, date, lastUpdated,
      isLoading, isRefreshing, isGeneratingExcel,
      isGeneratingDriverCsv,
      isGeneratingJoiningBonusCsv,
      excelGenerated, excelGeneratedAt,
      error, refreshResult, expandedRows,
      payoutFile, isUploading, payoutResult, payoutError,
      cashModalOpen, cashMode, vehicleIdInput, vehicleCashMode,
      cashOperationDate,
      vehicleTrips, selectedTripId,
      isLoadingVehicleTrips, isSavingCashPayment,
      cashActionMessage, cashActionError,
    } = this.state;

    const isLocked = excelGenerated;
    const anyBusy  = isLoading || isRefreshing || isGeneratingExcel || isGeneratingDriverCsv || isSavingCashPayment;

    return (
      <div>
        {/* ── Header bar ── */}
        <Row className="mb-3 align-items-center">
          <Col sm={6}>
            <h6 className="mb-0">
              <i className="fa fa-money mr-2 text-success" />
              Driver Pay —{' '}
              <span className="text-muted">{date || new Date().toLocaleDateString('en-IN')}</span>
            </h6>
            {lastUpdated && (
              <small className="text-muted">
                Last updated: {lastUpdated.toLocaleTimeString('en-IN')}
              </small>
            )}
          </Col>
          <Col sm={6}>
            <div className="d-flex justify-content-end flex-wrap" style={{ gap: '8px' }}>
              <Button
                color="light"
                size="sm"
                onClick={this.fetchTodayData}
                disabled={anyBusy || isLocked}
              >
                <i className="fa fa-eye mr-1" />
                Load
              </Button>
              <Button
                color="warning"
                size="sm"
                onClick={this.handleRefresh}
                disabled={anyBusy || isLocked}
              >
                {isRefreshing ? (
                  <>
                    <Spinner size="sm" className="mr-1" />
                    Recalculating…
                  </>
                ) : (
                  <>
                    <i className="fa fa-refresh mr-1" />
                    Update &amp; Recalculate
                  </>
                )}
              </Button>
              <Button
                color="success"
                size="sm"
                onClick={this.handleGenerateExcel}
                disabled={anyBusy || isLocked || records.length === 0}
              >
                {isGeneratingExcel ? (
                  <>
                    <Spinner size="sm" className="mr-1" />
                    Generating…
                  </>
                ) : isLocked ? (
                  <>
                    <i className="fa fa-lock mr-1" />
                    Files Generated
                  </>
                ) : (
                  <>
                    <i className="fa fa-file-text-o mr-1" />
                    Generate CSV Files
                  </>
                )}
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={this.handleGenerateDriverCsv}
                disabled={anyBusy}
              >
                {isGeneratingDriverCsv ? (
                  <>
                    <Spinner size="sm" className="mr-1" />
                    Generating…
                  </>
                ) : (
                  <>
                    <i className="fa fa-user mr-1" />
                    Generate Driver CSV
                  </>
                )}
              </Button>
              <Button
                color="dark"
                size="sm"
                onClick={this.toggleCashModal}
                disabled={anyBusy}
              >
                <i className="fa fa-money mr-1" />
                Cash Payments
              </Button>
              <Button
                color="info"
                size="sm"
                onClick={this.handleGenerateJoiningBonusCsv}
                disabled={anyBusy || isGeneratingJoiningBonusCsv}
              >
                {isGeneratingJoiningBonusCsv ? (
                  <>
                    <Spinner size="sm" className="mr-1" />
                    Generating…
                  </>
                ) : (
                  <>
                    <i className="fa fa-gift mr-1" />
                    Generate Joining Bonus CSV
                  </>
                )}
              </Button>
            </div>
          </Col>
        </Row>

        {/* ── Payout Upload (always visible) ── */}
        {(
          <div
            style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '12px',
            }}
          >
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
              <i className="fa fa-upload text-success mr-2" />
              <strong style={{ marginRight: '8px' }}>Disburse via Cashfree Payouts</strong>
              <input
                ref={this.fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={this.handlePayoutFileChange}
                id="payout-csv-input"
              />
              <label
                htmlFor="payout-csv-input"
                style={{
                  cursor: 'pointer',
                  padding: '4px 12px',
                  border: '1px solid #16a34a',
                  borderRadius: '4px',
                  color: '#16a34a',
                  fontSize: '13px',
                  marginBottom: 0,
                  background: '#fff',
                }}
              >
                {payoutFile ? payoutFile.name : 'Choose CSV…'}
              </label>
              <Button
                color="success"
                size="sm"
                onClick={this.handlePayoutUpload}
                disabled={!payoutFile || isUploading}
              >
                {isUploading ? (
                  <><Spinner size="sm" className="mr-1" />Disbursing…</>
                ) : (
                  <><i className="fa fa-paper-plane mr-1" />Upload &amp; Disburse</>
                )}
              </Button>
            </div>

            {payoutError && (
              <Alert color="danger" className="mt-2 mb-0 py-2">
                <i className="fa fa-exclamation-triangle mr-1" />
                {payoutError}
              </Alert>
            )}

            {payoutResult && (
              <div className="mt-2">
                <Alert
                  color={payoutResult.failed === 0 ? 'success' : 'warning'}
                  className="mb-2 py-2"
                >
                  <i className={`fa ${payoutResult.failed === 0 ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2`} />
                  <strong>Payout complete.</strong>{' '}
                  {payoutResult.success}/{payoutResult.total} succeeded
                  {payoutResult.failed > 0 && (
                    <span className="text-danger ml-2">· {payoutResult.failed} failed</span>
                  )}
                </Alert>

                {payoutResult.results && payoutResult.results.length > 0 && (
                  <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                    <Table responsive size="sm" style={{ fontSize: '12px', marginBottom: 0 }}>
                      <thead className="thead-light">
                        <tr>
                          <th>Transfer ID</th>
                          <th>VPA</th>
                          <th className="text-right">Amount</th>
                          <th>Status</th>
                          <th>UTR / Ref</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payoutResult.results.map((r) => (
                          <tr key={r.transfer_id}>
                            <td className="text-muted" style={{ fontFamily: 'monospace' }}>
                              {r.transfer_id}
                            </td>
                            <td>{r.vpa}</td>
                            <td className="text-right">₹{parseFloat(r.amount).toFixed(2)}</td>
                            <td>
                              <Badge color={r.status === 'SUCCESS' ? 'success' : 'danger'}>
                                {r.status}
                              </Badge>
                            </td>
                            <td className="text-muted" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                              {r.utr || r.cashfree_ref_id || '—'}
                            </td>
                            <td style={{ color: '#dc2626', fontSize: '11px' }}>
                              {r.error || ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Excel lock banner ── */}
        {isLocked && (
          <Alert color="warning" className="mb-3 py-2">
            <i className="fa fa-lock mr-2" />
            <strong>Files generated</strong>
            {excelGeneratedAt && (
              <span className="ml-1 text-muted" style={{ fontSize: '0.85rem' }}>
                at {new Date(excelGeneratedAt).toLocaleTimeString('en-IN')}
              </span>
            )}
            . This day is locked — recalculation and re-export are disabled.
          </Alert>
        )}

        {/* ── Refresh result banner ── */}
        {refreshResult && (
          <Alert color="success" className="mb-3 py-2">
            <i className="fa fa-check-circle mr-2" />
            <strong>Recalculation done.</strong>{' '}
            {refreshResult.driversProcessed} driver(s) processed &nbsp;|&nbsp;
            {refreshResult.deletedRows} old row(s) deleted &nbsp;|&nbsp;
            {refreshResult.resetBonusRows} bonus row(s) reset &nbsp;|&nbsp;
            Grand total: <strong>{this.formatCurrency(refreshResult.grandTotal)}</strong>
          </Alert>
        )}

        {/* ── Error ── */}
        {error && (
          <Alert color="danger" className="mb-3">
            <i className="fa fa-exclamation-triangle mr-2" />
            {error}
          </Alert>
        )}

        <Modal isOpen={cashModalOpen} toggle={this.toggleCashModal} size="md">
          <ModalHeader toggle={this.toggleCashModal}>Cash Payments</ModalHeader>
          <ModalBody>
            <Alert color="info" className="py-2">
              <i className="fa fa-info-circle mr-1" />
              Use this to quickly mark eligible trips as paid via cash for the selected date.
            </Alert>

            <FormGroup>
              <Label for="cashOperationDate">Operation Date</Label>
              <Input
                id="cashOperationDate"
                type="date"
                value={cashOperationDate}
                onChange={(e) => this.setState({
                  cashOperationDate: e.target.value,
                  vehicleTrips: [],
                  selectedTripId: '',
                  cashActionError: null,
                  cashActionMessage: null,
                })}
              />
            </FormGroup>

            <FormGroup tag="fieldset">
              <Label className="d-block mb-2">Choose action</Label>
              <FormGroup check className="p-2 border rounded">
                <Label check>
                  <Input
                    type="radio"
                    name="cashMode"
                    checked={cashMode === 'all_today'}
                    onChange={() => this.setState({ cashMode: 'all_today', cashActionError: null, cashActionMessage: null })}
                  />{' '}
                  Mark all trips for the day paid via cash
                </Label>
              </FormGroup>
              <FormGroup check className="mt-2 p-2 border rounded">
                <Label check>
                  <Input
                    type="radio"
                    name="cashMode"
                    checked={cashMode === 'specific_driver'}
                    onChange={() => this.setState({ cashMode: 'specific_driver', cashActionError: null, cashActionMessage: null })}
                  />{' '}
                  Mark specific driver (vehicle) paid via cash
                </Label>
              </FormGroup>
            </FormGroup>

            {cashMode === 'specific_driver' && (
              <div className="mt-3 p-3 border rounded bg-light">
                <FormGroup>
                  <Label for="cashVehicleId">Vehicle ID</Label>
                  <Input
                    id="cashVehicleId"
                    type="number"
                    min="1"
                    value={vehicleIdInput}
                    onChange={(e) => this.setState({ vehicleIdInput: e.target.value, vehicleTrips: [], selectedTripId: '' })}
                    placeholder="Enter vehicle ID"
                  />
                </FormGroup>

                <FormGroup tag="fieldset">
                  <Label className="d-block mb-2">Apply to</Label>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="radio"
                        name="vehicleCashMode"
                        checked={vehicleCashMode === 'all_trips'}
                        onChange={() => this.setState({ vehicleCashMode: 'all_trips', cashActionError: null, cashActionMessage: null })}
                      />{' '}
                      All trips of this vehicle for the selected day
                    </Label>
                  </FormGroup>
                  <FormGroup check className="mt-2">
                    <Label check>
                      <Input
                        type="radio"
                        name="vehicleCashMode"
                        checked={vehicleCashMode === 'particular_trip'}
                        onChange={() => this.setState({ vehicleCashMode: 'particular_trip', cashActionError: null, cashActionMessage: null })}
                      />{' '}
                      Particular trip
                    </Label>
                  </FormGroup>
                </FormGroup>

                {vehicleCashMode === 'particular_trip' && (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: '8px' }}>
                      <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                        <Button
                          color="primary"
                          outline
                          size="sm"
                          onClick={this.loadVehicleTrips}
                          disabled={isLoadingVehicleTrips || !vehicleIdInput}
                        >
                          {isLoadingVehicleTrips ? (
                            <><Spinner size="sm" className="mr-1" />Loading trips…</>
                          ) : (
                            <><i className="fa fa-refresh mr-1" />Load Trips</>
                          )}
                        </Button>
                        <small className="text-muted">Fetch trips for selected date + vehicle</small>
                      </div>
                      <Badge color={vehicleTrips.length > 0 ? 'success' : 'secondary'} pill>
                        {vehicleTrips.length} trip(s)
                      </Badge>
                    </div>

                    {vehicleTrips.length > 0 && (
                      <div
                        className="mb-2 p-2 border rounded"
                        style={{ maxHeight: '140px', overflowY: 'auto', background: '#fff' }}
                      >
                        {vehicleTrips.map((trip) => (
                          <div key={`preview-${trip.trip_id}`} className="mb-1" style={{ fontSize: '12px' }}>
                            <strong>{`Trip ${trip.trip_id}`}</strong>
                            <span className="text-muted">{` — ${trip.pickup_location || 'N/A'} → ${trip.drop_location || 'N/A'}`}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <FormGroup>
                      <Label for="cashTripSelect">Select Trip</Label>
                      <Input
                        id="cashTripSelect"
                        type="select"
                        value={selectedTripId}
                        disabled={vehicleTrips.length === 0}
                        onChange={(e) => this.setState({ selectedTripId: e.target.value })}
                      >
                        <option value="">Select a trip</option>
                        {vehicleTrips.map((trip) => (
                          <option key={trip.trip_id} value={trip.trip_id}>
                            {`Trip ${trip.trip_id} — ${trip.pickup_location || 'N/A'} → ${trip.drop_location || 'N/A'}`}
                          </option>
                        ))}
                      </Input>
                      <small className="text-muted">
                        Only selected day’s completed/cancelled-by-customer trips are listed.
                      </small>
                    </FormGroup>
                  </>
                )}
              </div>
            )}

            {cashActionError && (
              <Alert color="danger" className="mb-0 py-2">
                <i className="fa fa-exclamation-triangle mr-1" />
                {cashActionError}
              </Alert>
            )}
            {cashActionMessage && (
              <Alert color="success" className="mb-0 py-2">
                <i className="fa fa-check-circle mr-1" />
                {cashActionMessage}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.handleCashPaymentSubmit} disabled={isSavingCashPayment}>
              {isSavingCashPayment ? 'Saving...' : 'Apply'}
            </Button>{' '}
            <Button color="secondary" onClick={this.toggleCashModal} disabled={isSavingCashPayment}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* ── Loading ── */}
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading driver pay data…</p>
          </div>
        ) : records.length === 0 ? (
          <Alert color="info">
            <i className="fa fa-info-circle mr-2" />
            No driver pay data for today yet. Click <strong>Update &amp; Recalculate</strong> to generate.
          </Alert>
        ) : (
          <>
            {/* ── Grand total pill ── */}
            <div className="mb-3">
              <Badge
                color="success"
                style={{ fontSize: '1rem', padding: '6px 14px', borderRadius: '20px' }}
              >
                Grand Total: {this.formatCurrency(grandTotal)}
              </Badge>
              <span className="text-muted ml-3" style={{ fontSize: '0.85rem' }}>
                {records.length} driver(s)
              </span>
            </div>

            {/* ── Table ── */}
            <Table responsive striped size="sm">
              <thead className="thead-light">
                <tr>
                  <th style={{ width: 32 }} />
                  <th>#</th>
                  <th>Driver</th>
                  <th className="text-center">Trips</th>
                  <th className="text-right">Active KM</th>
                  <th className="text-right">Dead KM</th>
                  <th className="text-right">Estimate Pay</th>
                  <th className="text-right">Labour Pay</th>
                  <th className="text-right">Trip Pay</th>
                  <th className="text-right">Bonus</th>
                  <th className="text-right">Total Pay</th>
                  <th className="text-center">Paid?</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec, idx) => {
                  const isExpanded = expandedRows.has(rec.driver_id);
                  const trips = rec.km_details || [];
                  return (
                    <React.Fragment key={rec.driver_id}>
                      {/* ── Main row ── */}
                      <tr
                        style={{ cursor: trips.length > 0 ? 'pointer' : 'default' }}
                        onClick={() => trips.length > 0 && this.toggleRow(rec.driver_id)}
                      >
                        <td className="text-center" style={{ color: '#6b7280', userSelect: 'none' }}>
                          {trips.length > 0 && (
                            <span style={{
                              display: 'inline-block',
                              transition: 'transform 0.2s',
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              fontSize: '12px',
                            }}>▼</span>
                          )}
                        </td>
                        <td className="text-muted">{idx + 1}</td>
                        <td>
                          <strong>{rec.driver_name}</strong>
                          <br />
                          <small className="text-muted">Vehicle: {rec.vehicle_id || '—'}</small>
                        </td>
                        <td className="text-center">
                          <Badge color="info">{rec.total_trips}</Badge>
                        </td>
                        <td className="text-right">{this.formatKm(rec.total_active)}</td>
                        <td className="text-right">{this.formatKm(rec.total_dead)}</td>
                        <td className="text-right">{this.formatCurrency(rec.estimated_pay)}</td>
                        <td className="text-right">
                          {rec.labour_pay > 0 ? (
                            <span className="text-primary font-weight-bold">
                              {this.formatCurrency(rec.labour_pay)}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-right">{this.formatCurrency(rec.base_pay)}</td>
                        <td className="text-right">
                          {rec.bonus_pay > 0 ? (
                            <span className="text-success font-weight-bold">
                              {this.formatCurrency(rec.bonus_pay)}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-right font-weight-bold">
                          {this.formatCurrency(rec.total_pay)}
                        </td>
                        <td className="text-center">
                          <Badge color={rec.is_paid ? 'success' : 'secondary'}>
                            {rec.is_paid ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                        <td>
                          <small className="text-muted">
                            {rec.updated_at
                              ? new Date(rec.updated_at).toLocaleTimeString('en-IN')
                              : '—'}
                          </small>
                        </td>
                      </tr>

                      {/* ── Expanded trip details ── */}
                      {isExpanded && trips.length > 0 && (
                        <tr>
                          <td colSpan={13} style={{ padding: 0, background: '#f8fafc' }}>
                            <div style={{ padding: '12px 24px 12px 48px' }}>
                              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                                    <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 600 }}>Trip ID</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 600 }}>Active KM</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 600 }}>Initial Dead</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 600 }}>Final Dead</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 600 }}>Total Dead</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 600 }}>Estimate</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 600 }}>Labour</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 600 }}>Trip Pay</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {trips.map((t, ti) => {
                                    const totalDead = (t.initial_dead_km || 0) + (t.final_dead_km || 0);
                                    const estimatedCost = parseFloat(t.estimated_cost || 0);
                                    const labourPay = parseFloat(t.labour_pay || 0);
                                    const driverPay = parseFloat(t.driver_pay || (estimatedCost + labourPay));
                                    return (
                                      <tr
                                        key={ti}
                                        style={{ borderBottom: '1px solid #f1f5f9', background: ti % 2 === 0 ? '#fff' : '#f8fafc' }}
                                      >
                                        <td style={{ padding: '4px 10px' }}>
                                          <span style={{
                                            background: '#dbeafe', color: '#1d4ed8',
                                            borderRadius: '4px', padding: '1px 7px', fontWeight: 600
                                          }}>#{t.trip_id}</span>
                                        </td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right', color: '#16a34a', fontWeight: 500 }}>
                                          {(t.active_km || 0).toFixed(1)} km
                                        </td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right', color: '#9ca3af' }}>
                                          {(t.initial_dead_km || 0).toFixed(1)} km
                                        </td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right', color: '#9ca3af' }}>
                                          {(t.final_dead_km || 0).toFixed(1)} km
                                        </td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right', color: '#dc2626', fontWeight: 500 }}>
                                          {totalDead.toFixed(1)} km
                                        </td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right', color: '#0f172a', fontWeight: 500 }}>
                                          {this.formatCurrency(estimatedCost)}
                                        </td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right', color: '#2563eb', fontWeight: 500 }}>
                                          {this.formatCurrency(labourPay)}
                                        </td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>
                                          {this.formatCurrency(driverPay)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-weight-bold">
                  <td colSpan="10" className="text-right">Grand Total</td>
                  <td className="text-right text-success">
                    {this.formatCurrency(grandTotal)}
                  </td>
                  <td colSpan="2" />
                </tr>
              </tfoot>
            </Table>
          </>
        )}
      </div>
    );
  }
}

export default DriverPayTab;
